import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faArrowLeft,
  faRetweet,
  faComment,
  faHeart,
  faQuoteRight,
  faEye,
  faBookmark,
  faExternalLinkAlt,
  faCheckCircle,
  faUser,
  faChevronLeft,
  faChevronRight,
  faGlobe,
  faFire,
  faClock,
  faUsers,
  faNewspaper,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL_INFLUENCER;

/* ── Query type config ─────────────────────────────────────────── */
const QUERY_TYPES = [
  { value: "Top", label: "Top", icon: faFire, color: "#E65100" },
  { value: "Latest", label: "Latest", icon: faClock, color: "#1565C0" },
  { value: "People", label: "People", icon: faUsers, color: "#7B1FA2" },
  { value: "Media", label: "Media", icon: faNewspaper, color: "#2E7D32" },
];

const GeneralTweetSearch = () => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [queryType, setQueryType] = useState("Top");
  const [page, setPage] = useState(1);

  const [tweets, setTweets] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalTweets, setTotalTweets] = useState(0);
  const [currentQuery, setCurrentQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  // ── Fetch tweets ─────────────────────────────────────────────────
  const fetchTweets = useCallback(async (query, type, pg) => {
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setSearched(true);

    try {
      const res = await axios.get(
        `${API_URL}/search/tweets?query=${encodeURIComponent(
          query.trim()
        )}&query_type=${encodeURIComponent(type)}&page=${pg}`
      );

      if (res.data?.status === "success") {
        setTweets(res.data.tweets || []);
        setHasNextPage(res.data.has_next_page || false);
        setTotalTweets(res.data.total_tweets || 0);
        setCurrentQuery(query.trim());
      } else {
        setError("No tweets found for this query.");
        setTweets([]);
      }
    } catch (err) {
      console.error("Error searching tweets:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to search tweets. Please try again."
      );
      setTweets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setPage(1);
    fetchTweets(searchQuery, queryType, 1);
  };

  const handleTypeChange = (type) => {
    setQueryType(type);
    if (currentQuery) {
      setPage(1);
      fetchTweets(currentQuery, type, 1);
    }
  };

  const goToPage = (newPage) => {
    setPage(newPage);
    fetchTweets(currentQuery, queryType, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Helpers ──────────────────────────────────────────────────────
  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEngagementLevel = (metrics) => {
    if (!metrics) return { label: "Low", color: "#DC3545" };
    const total =
      (metrics.like_count || 0) +
      (metrics.retweet_count || 0) +
      (metrics.reply_count || 0);
    if (total >= 1000) return { label: "Viral", color: "#28A745" };
    if (total >= 100) return { label: "High", color: "#FFC107" };
    if (total >= 10) return { label: "Medium", color: "#FF8C00" };
    return { label: "Low", color: "#6C757D" };
  };

  // ── Metric Pill ──────────────────────────────────────────────────
  const MetricPill = ({ icon, value, label }) => (
    <span
      style={{ display: "flex", alignItems: "center", gap: 5 }}
      title={label}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: "0.78rem" }} />
      {formatNumber(value)}
    </span>
  );

  // ── Tweet Card ───────────────────────────────────────────────────
  const TweetCard = ({ tweet }) => {
    const metrics = tweet.metrics || {};
    const engagement = getEngagementLevel(metrics);
    const author = tweet.author || {};

    return (
      <div
        className="card fade-in"
        style={{
          padding: 0,
          marginBottom: 16,
          overflow: "hidden",
          borderLeft: `4px solid ${engagement.color}`,
        }}
      >
        {/* Author Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px 0",
          }}
        >
          {/* Profile Picture */}
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "var(--background-light)",
              border: "2px solid var(--border-color)",
            }}
          >
            {author.profile_picture ? (
              <img
                src={author.profile_picture.replace("_normal", "_bigger")}
                alt={author.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ color: "var(--text-gray)" }}
                />
              </div>
            )}
          </div>

          {/* Name + Username */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  color: "var(--text-dark)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {author.name || "Unknown"}
              </span>
              {author.is_blue_verified && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#1DA1F2", fontSize: "0.8rem" }}
                />
              )}
              <span
                style={{
                  fontSize: "0.82rem",
                  color: "var(--text-gray)",
                }}
              >
                @{author.username || "unknown"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 2,
              }}
            >
              <span
                style={{
                  fontSize: "0.78rem",
                  color: "var(--text-gray)",
                }}
              >
                {formatDate(tweet.tweet_created_at)}
              </span>
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "var(--text-gray)",
                }}
              >
                · {formatNumber(author.followers)} followers
              </span>
              {/* Engagement Badge */}
              <span
                style={{
                  backgroundColor: `${engagement.color}18`,
                  color: engagement.color,
                  border: `1px solid ${engagement.color}40`,
                  padding: "1px 8px",
                  borderRadius: 10,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                {engagement.label}
              </span>
            </div>
          </div>
        </div>

        {/* Tweet Text */}
        <div style={{ padding: "12px 20px 14px" }}>
          <p
            style={{
              fontSize: "0.95rem",
              color: "var(--text-dark)",
              lineHeight: 1.65,
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {tweet.text}
          </p>
        </div>

        {/* Metrics Row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            fontSize: "0.82rem",
            color: "var(--text-gray)",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-color)",
            alignItems: "center",
            backgroundColor: "var(--background-light)",
          }}
        >
          <MetricPill icon={faEye} value={metrics.view_count} label="Views" />
          <MetricPill icon={faHeart} value={metrics.like_count} label="Likes" />
          <MetricPill
            icon={faRetweet}
            value={metrics.retweet_count}
            label="Retweets"
          />
          <MetricPill
            icon={faComment}
            value={metrics.reply_count}
            label="Replies"
          />
          <MetricPill
            icon={faQuoteRight}
            value={metrics.quote_count}
            label="Quotes"
          />
          <MetricPill
            icon={faBookmark}
            value={metrics.bookmark_count}
            label="Bookmarks"
          />

          {tweet.url && (
            <a
              href={tweet.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: "auto",
                color: "var(--primary-color)",
                fontWeight: 600,
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: "0.82rem",
              }}
            >
              View on X
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                style={{ fontSize: "0.7rem" }}
              />
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="dashboard-header">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate("/influencer-leaderboard")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 8,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Leaderboard
            </button>
            <div>
              <h1 className="dashboard-title" style={{ marginBottom: 2 }}>
                <FontAwesomeIcon
                  icon={faGlobe}
                  style={{ marginRight: 10, color: "var(--primary-color)" }}
                />
                General Tweet Search
              </h1>
              <p className="dashboard-subtitle" style={{ margin: 0 }}>
                Search Twitter for tweets on any topic, keyword, or hashtag.
              </p>
            </div>
          </div>
        </div>

        {/* ── Search Bar ───────────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <form
            onSubmit={handleSearch}
            style={{ display: "flex", gap: 12, alignItems: "center" }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-gray)",
                  fontSize: "0.9rem",
                }}
              />
              <input
                type="text"
                placeholder="Search tweets by keyword, hashtag, or topic..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (error) setError("");
                }}
                style={{
                  paddingLeft: 42,
                  height: 46,
                  fontSize: "0.95rem",
                  borderRadius: 10,
                }}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !searchQuery.trim()}
              style={{ height: 46, minWidth: 130 }}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} style={{ marginRight: 6 }} />
                  Search
                </>
              )}
            </button>
          </form>

          {error && !loading && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                backgroundColor: "#FFF3CD",
                color: "#856404",
                borderRadius: 8,
                fontSize: "0.88rem",
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* ── Query Type Tabs ──────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {QUERY_TYPES.map((qt) => {
                const isActive = queryType === qt.value;
                return (
                  <button
                    key={qt.value}
                    onClick={() => handleTypeChange(qt.value)}
                    style={{
                      padding: "8px 20px",
                      borderRadius: 20,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      backgroundColor: isActive
                        ? qt.color
                        : "var(--background-light)",
                      color: isActive ? "#fff" : "var(--text-gray)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <FontAwesomeIcon icon={qt.icon} />
                    {qt.label}
                  </button>
                );
              })}
            </div>

            {/* Results count */}
            {searched && !loading && tweets.length > 0 && (
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-gray)",
                  fontWeight: 600,
                }}
              >
                {totalTweets} tweet{totalTweets !== 1 ? "s" : ""} found
              </span>
            )}
          </div>
        </div>

        {/* ── Active Query Badge ───────────────────────────────── */}
        {currentQuery && !loading && tweets.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 16,
              padding: "0 4px",
            }}
          >
            <span
              style={{
                fontSize: "0.88rem",
                color: "var(--text-gray)",
                fontWeight: 500,
              }}
            >
              Results for
            </span>
            <span
              style={{
                backgroundColor: "var(--primary-color)",
                color: "#fff",
                padding: "4px 14px",
                borderRadius: 16,
                fontSize: "0.82rem",
                fontWeight: 700,
              }}
            >
              "{currentQuery}"
            </span>
            <span
              style={{
                fontSize: "0.82rem",
                color: "var(--text-gray)",
                fontWeight: 500,
              }}
            >
              · Page {page}
            </span>
          </div>
        )}

        {/* ── Loading ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
            <p style={{ color: "var(--text-gray)", marginTop: 8 }}>
              Searching tweets...
            </p>
          </div>
        )}

        {/* ── Tweets List ──────────────────────────────────────── */}
        {!loading &&
          tweets.map((tweet) => (
            <TweetCard key={tweet.tweet_id} tweet={tweet} />
          ))}

        {/* ── Pagination ───────────────────────────────────────── */}
        {!loading && tweets.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              marginBottom: 24,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "var(--text-gray)",
                fontWeight: 600,
              }}
            >
              Page {page} · {totalTweets} tweet{totalTweets !== 1 ? "s" : ""}{" "}
              found
            </span>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Previous
              </button>

              <button
                className="btn btn-primary btn-sm"
                disabled={!hasNextPage}
                onClick={() => goToPage(page + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}

        {/* ── Initial State ────────────────────────────────────── */}
        {!loading && tweets.length === 0 && !error && !searched && (
          <div className="card" style={{ textAlign: "center", padding: 48 }}>
            <FontAwesomeIcon
              icon={faGlobe}
              style={{
                fontSize: "2.5rem",
                color: "var(--border-color)",
                marginBottom: 16,
              }}
            />
            <h3 style={{ color: "var(--text-gray)", fontWeight: 600 }}>
              Search Twitter
            </h3>
            <p style={{ color: "var(--text-gray)", fontSize: "0.9rem" }}>
              Enter a keyword, hashtag, or topic above to discover tweets from
              across the platform.
            </p>
          </div>
        )}

        {/* ── No results ───────────────────────────────────────── */}
        {!loading && tweets.length === 0 && searched && !error && (
          <div className="card" style={{ textAlign: "center", padding: 40 }}>
            <p
              style={{ color: "var(--text-gray)", fontWeight: 600, margin: 0 }}
            >
              No tweets found for "{currentQuery}" on page {page}.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default GeneralTweetSearch;